// client/src/services/aiReview.ts

export interface ScheduleConfig {
  intervalMinutes?: number;   // default: 15
  windowStartHour?: number;   // 0-23, default: 0
  windowEndHour?: number;     // 0-23 exclusive, default: 24
  timezone?: string;          // IANA tz, default: 'America/Chicago'
  daysOfWeek?: number[];      // 0=Sun…6=Sat, default: all
  blackoutRanges?: { from: string; to: string }[];  // MM-DD annual
}

export interface PdfConfig {
  primaryColor?: string;   // h2 headers, metadata labels, table header text (default: #1a3a6b)
  headerBg?: string;       // header bar background (default: #1e293b)
  firmName?: string;       // shown in header sub-line (default: "BMSS Automated Quality Review")
  letterheadUrl?: string;  // full-width image replacing the header bar
}

export interface AIReviewProcess {
  id: number;
  name: string;
  process_type: 'full_return' | 'delivery_doc';
  is_active: boolean;
  pe_work_status: string | null;
  return_types: string | null;    // JSON array string e.g. '["S","C"]'
  tax_years: string | null;       // JSON array string e.g. '[2024,2025]'
  export_scope: 'federal' | 'state' | 'both';
  export_config: string | null;
  system_prompt: string | null;
  user_template: string | null;
  pdf_config: string | null;        // JSON-serialized PdfConfig
  schedule_config: string | null;   // JSON-serialized ScheduleConfig
  model: string;
  output_mode: 'memo' | 'html';
  email_to_roles: string | null;  // JSON array string e.g. '["partner","current_staff"]'
  email_to_addrs: string | null;  // JSON array string e.g. '["boss@firm.com"]'
  poll_interval: number;
  dedup_days: number;
  created_at: string;
  updated_at: string;
  // Last-run stats — joined from ai_review_runs by the list endpoint
  last_run_at?: string | null;
  last_run_type?: 'schedule' | 'manual' | null;
  last_reviewed?: number | null;
  last_skipped?: number | null;
  last_failed?: number | null;
  last_cost?: number | null;
}

export interface AIReviewRun {
  id: number;
  process_id: number;
  process_name: string;
  run_type: 'schedule' | 'manual' | 'ondemand';
  started_at: string;
  finished_at: string | null;
  matched: number;
  reviewed: number;
  skipped: number;
  failed: number;
  total_cost: number;
  errors: string | null;  // JSON: [{jobIdx, clientCode, clientName, error}]
}

export interface AIReviewResultMeta {
  blobName: string;
  jobIdx: number;
  reviewDate: string;
  sizeBytes: number;
}

export interface AIReviewResult {
  processId: number;
  processName: string;
  jobIdx: number;
  clientCode: string;
  clientName: string;
  jobName: string;
  cchReturnId: string;
  reviewedAt: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number;
  callCount: number;
  content: string;
}

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Server error ${res.status}`);
  }
  return res.json();
}

export async function listProcesses(): Promise<AIReviewProcess[]> {
  return apiFetch('/api/ai-review/processes');
}

export async function saveProcess(data: Partial<AIReviewProcess>): Promise<{ id: number }> {
  return apiFetch('/api/ai-review/processes', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export interface AIReviewFailure {
  blobName:    string;
  jobIdx:      number;
  failDate:    string;
  processId?:  number;
  processName?: string;
  clientCode?: string;
  clientName?: string;
  jobName?:    string;
  taxyear?:    number | string;
  model?:      string;
  failedAt?:   string;
  error?:      string;
  // saved result exists at ai-review-results path (analysis was saved before PDF failed)
  hasSavedResult?: boolean;
}

export async function listResults(processId: number): Promise<AIReviewResultMeta[]> {
  return apiFetch(`/api/ai-review/results?processId=${processId}`);
}

export async function listFailures(processId: number): Promise<AIReviewFailure[]> {
  return apiFetch(`/api/ai-review/results?processId=${processId}&failures=true`);
}

export async function retryReview(
  processId: number,
  jobIdx: number,
  action: 'resend' | 'rerun',
  date?: string,
  extraRecipients?: string[],
): Promise<{ ok: boolean; message?: string; error?: string }> {
  return apiFetch('/api/ai-review/retry', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ processId, jobIdx, date, action, extraRecipients: extraRecipients ?? [] }),
  });
}

export async function getResult(processId: number, jobIdx: number): Promise<AIReviewResult> {
  return apiFetch(`/api/ai-review/results?processId=${processId}&jobIdx=${jobIdx}`);
}

export async function runReview(processId: number, job: Record<string, unknown>): Promise<{ ok: boolean; estimatedCost?: number; error?: string }> {
  return apiFetch('/api/ai-review/run', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ processId, job }),
  });
}

export async function triggerProcess(processId: number): Promise<{ ok: boolean; ran: number; skipped: number; failed: number; message?: string }> {
  return apiFetch('/api/ai-review/trigger', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ processId }),
  });
}

export async function triggerOnDemand(
  processId: number,
  clientCode: string,
  applyStatusFilter: boolean,
  extraRecipients: string[],
): Promise<{ ok: boolean; runId?: number; queued?: number; message?: string; debugMode?: boolean }> {
  return apiFetch('/api/ai-review/trigger', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ processId, clientCode, applyStatusFilter, extraRecipients }),
  });
}

export async function listRuns(processId?: number, limit = 50): Promise<AIReviewRun[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (processId) params.set('processId', String(processId));
  return apiFetch(`/api/ai-review/runs?${params}`);
}

export async function getRunLog(processId: number, date: string, live = false): Promise<string> {
  const params = live
    ? `processId=${processId}&live=true`
    : `processId=${processId}&date=${date}`;
  const res = await fetch(`/api/ai-review/log?${params}`, { credentials: 'include' });
  if (res.status === 404) return live ? '' : '(No log for this date.)';
  if (!res.ok) throw new Error(`Server error ${res.status}`);
  return res.text();
}

export async function uploadLetterhead(file: File): Promise<{ url: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        // Strip the data URL prefix (e.g. "data:image/png;base64,")
        const base64 = (reader.result as string).split(',')[1];
        const result = await apiFetch<{ url: string }>('/api/ai-review/upload-letterhead', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ filename: file.name, contentType: file.type, data: base64 }),
        });
        resolve(result);
      } catch (e) {
        reject(e);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}
